import { LinkType } from 'src/services/common/common.types';
import type { Organization } from 'src/services/auth/auth.types';
import type { EntityLabel } from 'src/services/utils/utils.types';
import type { PageOptions, PaginatedResponse } from 'src/services/api.types';
import type { ExporterFieldMappingDto } from 'src/services/organisation/organisation.types';
import { ApplicationStage, ApplicationStatus } from 'src/services/applications/applications.types';

import type { TenantType } from 'src/types/enums';

import { BoolNode } from '../common.types';
import type { AttachedThread } from '../threads/threads.types';
import type { DocumentFile, DocumentType } from '../files/file.types';
import type { IJob, Skill, VisibilityRule } from '../jobs/jobs.types';
import { UpdateCustomFieldValueDto } from '../custom-fields/custom-field.types';

// JT1-1534: Application -> CRM candidate write-back. When an agency user edits one of these
// structured fields on an application and it differs from their own-org CRM record, the API returns
// a conflict for the recruiter to confirm; confirming re-issues the save with `apply_write_back`.
export type WriteBackFieldKey =
  | 'salary_expectations'
  | 'notice_period'
  | 'job_types'
  | 'work_styles'
  | 'work_rights'
  | 'education_level';

export interface WriteBackConflict {
  field: WriteBackFieldKey;
  crm_value: unknown;
  new_value: unknown;
}

export interface CandidateProfileWriteBack {
  conflicts: WriteBackConflict[];
}

export type SetCandidateProfileResponse = Partial<CandidateProfile> & {
  write_back?: CandidateProfileWriteBack;
};

export interface GetCandidateJobsProps {
  candidateId: string;
  page: number;
  per_page: number;
  search_query?: string;
}

export interface IMinimalCandidateProfile {

  id: string;

  first_name: string;

  last_name: string;

  position_title: string;

  updated_at: Date;

}

export interface ICandidateDocument {
  id: string;
  document: DocumentFile;
  created_at: Date;
}

export interface CandidateProfile {
  id?: string;
  type?: string;
  sections?: CandidateProfileSection[];
  title?: string;
  first_name?: string;
  email?: string;
  completion_percentage?: number;
  phone_number?: string;
  country_code?: string;
  location?: string;
  position_title?: string;
  last_name?: string;
  // send new "unclaimed" file to update profile picture or explicit "null" to remove existing image. undefined will ignore this field
  profile_image_id?: string | null | undefined;
  profile_image?: {
    id: string;
    public_path: string;
  };
  summary?: {
    content: string;
  };
  job_types?: CandidateProfileData[];
  work_styles?: CandidateProfileData[];
  education_level?: CandidateProfileData;
  work_rights?: CandidateProfileData[];
  salary_expectations?: ISalaryExpectation[];
  preferred_locations?: IPreferredLocation[];
  work_experiences?: IWorkExperience[];
  notice_period?: CandidateProfileData;
  skills?: Skill[];
  links?: IProfileLink[];
  education_history?: IEducationHistory[];
  availability_status?: CandidateAvailabilityStatus;
  available_from?: Date;

  updated_at: Date;
}

export interface ICandidateReferee {

  id: string;

  first_name: string;

  last_name: string;

  email: string;

  phone: string;

  country_code: string;

  relationship: string;

  position_title: string;

  company_name: string;

  referee_position: string;

  visibility_rule: VisibilityRule;

  work_experience: { id: string }

}

export interface ReferenceSubmissionResponse {

  id: string;

  question: string;

  answer: string;

  order: number;

}

export interface AddProfileRefereeDto {

  first_name: string;

  last_name: string;

  email: string;

  phone: string;

  country_code: string;

  relationship: string;

  position_title: string;

  company_name: string;

  referee_position: string;

  visibility_rule: VisibilityRule;

}

export enum OrganizationCandidateType {
  UNLINKED = 'unlinked',
  LINKED = 'linked',
}

export interface UpdateProfileRefereeDto {

  first_name?: string;

  last_name?: string;

  phone?: string;

  country_code?: string;

  relationship?: string;

  position_title?: string;

  company_name?: string;

  referee_position?: string;

  visibility_rule?: VisibilityRule;

}

export interface ICandidateApplication {
  id: string;
  job: IJob;
  status: ApplicationStatus;
  pre_finalization_stage: ApplicationStage;
  candidate_profile: CandidateProfile;
  stage: ApplicationStage;
  created_at: Date;
  updated_at: Date;
}

export type CandidateApplicationsResponse = PaginatedResponse<ICandidateApplication>;

export interface CandidateProfileSection {
  type?: CandidateProfileSectionType;
  section_type?: CandidateProfileSectionType;
  visibility_rule: VisibilityRule;
  access_context?: string;
}

export interface CandidateProfileData {
  type?: string;
  style?: string;
  work_right?: string;
}

export interface IPreferredLocation {
  place_id: string;
  address: string;
  latitude: number;
  longitude: number;
}

export interface ISalaryExpectation {
  min_amount: number;
  max_amount: number;
  period: string;
}

export interface ExportSettingsResponse {

  exporter_cover_field_mapping: ExporterFieldMappingDto[];

}

export interface IWorkExperience {
  id?: string;
  company_name: string;
  position_title: string;
  role_summary: string;
  start: DateObj;
  end: DateObj | undefined;
  item_order: number;
  duration_text?: string;
  duration_months?: number;
  verification_status?: ExperienceVerificationStatus;
  verification_type?: ExperienceVerificationType;
  verified_at?: string;
  verified_by_domain?: null;
}

export interface IProfileLink {
  type: LinkType;
  url: string;
}

export interface IEducationHistory {
  id?: string;
  institution_name: string;
  certification_name: string;
  start_year: number;
  end_year: number | null;
  level: EducationLevel;
}

export enum EducationLevel {
  HIGH_SCHOOL = 'high_school',
  CERTIFICATE = 'certificate',
  DIPLOMA = 'diploma',
  UNDERGRADUATE_DEGREE = 'undergraduate_degree',
  POSTGRADUATE_DEGREE = 'postgraduate_degree',
}

export enum WorkRightType {
  FULL_WORK_RIGHTS = "full_work_rights",
  SPONSORSHIP_REQUIRED = "sponsorship_required",
  RESTRICTED_VISA = "restricted_visa",
}

export interface CandidateImportJobData {
  file_ids: string[];
  create_invite: boolean;
}

export interface CandidateImportJobResults {
  failed_count: number;
  processed_count: number;
  success_count: number;
  total_count: number;
  import_results: {
    email: string,
    first_name: string,
    last_name: string,
    candidate_id: string,
  }[];
}

export enum CandidateImportJobStatus {
  PENDING = 'pending',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  FAILED = 'failed',
}

export type CandidateImportJob = {
  id: string;
  status: CandidateImportJobStatus;
  results: CandidateImportJobResults;
}

export interface IOrgCandidate {
  id: string;
  rating: number;
  privileged_access: boolean;
  email: string;
  phone_number?: string;
  country_code?: string;
  type: OrganizationCandidateType;
  special_information: string;
  labels: EntityLabel[];
  candidate_profile: CandidateProfile;
  synced_profile?: CandidateProfile;
  thread: AttachedThread;
  job_applications_count?: number;
  /** Parsed CV text for the record panel's resume-first body (JT1-1529). Null when unparsed or non-privileged. */
  resume_text?: string | null;
}

export interface OrgCandidateSalaryRange {
  min_amount: number;
  max_amount: number;
  period: string;
}

// Flat row returned by GET /my/candidates/list (the lean list read path, JT1-1527). Only the
// columns the shared list can display — not the full profile the heavy endpoint returns.
export interface IOrgCandidateListItem {
  id: string;
  rating: number;
  type: OrganizationCandidateType;
  privileged_access: boolean;
  first_name: string | null;
  last_name: string | null;
  position_title: string | null;
  // Recruiter-uploaded photo (or the initial live-profile clone) on the attached profile (JT1-1539).
  photo_url: string | null;
  location: string | null;
  email: string | null;
  phone_number: string | null;
  country_code: string | null;
  availability_status?: CandidateAvailabilityStatus;
  notice_period: string | null;
  salary_range: OrgCandidateSalaryRange | null;
  labels: EntityLabel[];
  last_activity_at: string | null;
  job_applications_count: number;
}

export interface GetOrgCandidatesQuery {
  page: PageOptions;
  search_query?: string;
  skills?: string[];
  availability_status?: CandidateAvailabilityStatus;
  labels?: string[];
  /** Per-column search values keyed by column key (item 3). Sent to the lean list as JSON. */
  column_filters?: Record<string, string>;
}

export interface AdvancedOrgCandidatesSearchRequest {
  page: PageOptions;
  search_dsl: BoolNode;
}

export interface LinkedOrgCandidate {
  id: string;
  candidate_profile: CandidateProfile;
}

export enum CandidateAvailabilityStatus {
  UNAVAILABLE = 'unavailable',
  AVAILABLE_NOW = 'available_now',
  AVAILABLE_SOON = 'available_soon',
  INTERESTED = 'interested',
}

export interface DateObj {
  month: number;
  year: number;
}

export enum CandidateProfileSectionType {
  EXECUTIVE_SUMMARY = 'executive_summary',
  JOB_TYPES = 'job_types',
  SALARY_EXPECTATIONS = 'salary_expectations',
  WORK_LOCATIONS = 'work_locations',
  WORK_STYLES = 'working_styles',
  WORK_RIGHTS = 'work_rights',
  NOTICE_PERIOD = 'notice_period',
  EDUCATION_LEVEL = 'education_level',
  EDUCATION_HISTORY = 'education_history',
  WORK_EXPERIENCES = 'work_experiences',
  SKILLS = 'skills',
  LINKS = 'links',
}

export enum ExperienceVerificationStatus {
  UNVERIFIED = 'unverified',
  PENDING = 'pending',
  VERIFIED = 'verified',
}

export enum ExperienceVerificationType {
  SYSTEM = 'system',
  INVITEE = 'invitee',
  REFERENCE = 'reference'
}

export enum ExpVerificationAction {
  ACCEPT = 'accept',
  REJECT = 'reject',
}

export enum CandidateInviteStatus {
  UNSENT = 'unsent',
  PENDING = 'pending',
  ACCEPTED = 'accepted',
  DECLINED = 'declined',
  EXPIRED = 'expired',
  CANCELLED = 'cancelled',
}

export enum CompanyInviteStatus {
  UNSENT = 'unsent',
  PENDING = 'pending',
  ACCEPTED = 'accepted',
  DECLINED = 'declined',
  EXPIRED = 'expired',
  CANCELLED = 'cancelled',
}

export type RequestVerificationProps = {
  work_experience_id: string;
  data: {
    email: string;
    first_name: string;
    last_name: string;
  };
};

export type ExperienceVerificationRequest = {
  id: string;
  company_name: string;
  position_title: string;
  role_summary: string;
  start_month: string;
  start_year: string;
  end_month: string;
  end_year: string;
  created_at: string;
  candidate_profile: Partial<CandidateProfile>;
};

export type CreateUnlinkedCandidateDto = {
  first_name: string;
  last_name: string;
  email?: string;
  phone_number?: string;
  country_code?: string;
  position_title?: string;
  special_information?: string;
  labels?: EntityLabel[];
  custom_fields?: UpdateCustomFieldValueDto;
  create_invite: boolean;
}

export type ImportUnlinkedCandidateDto = {
  document_id: string;
  create_invite?: boolean;
}

export type UpdateUnlinkedCandidateDto = {
  title?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone_number?: string;
  country_code?: string;
  position_title?: string;
  special_information?: string;
  custom_fields?: UpdateCustomFieldValueDto;
  labels?: EntityLabel[];
}

export type VerificationActionProps = {
  work_experience_id: string;
  data: {
    action: ExpVerificationAction;
    notes: string;
  };
};

export type UpdateAvailabilityProps = {
  status: CandidateAvailabilityStatus;
  available_from?: Date | null;
  withdraw_from_all?: boolean;
};


export interface CreateCandidateDocumentDto {
  file_id: string;
  name: string;
  type: DocumentType;
}


export type AvailabilityProfileResponse = {
  status: CandidateAvailabilityStatus;
  available_from: string;
  subscriber_accounts: string[];
  subscriber_organizations: string[];
};

export interface PublicCandidateInvite {
  email: string;
  first_name: string;
  last_name: string;
  status: CandidateInviteStatus;
  initiator: {
    id: string;
    company_name: string;
  };
  created_at: Date;
}

export interface OutgoingCandidateInvite {
  id: string;
  status: CandidateInviteStatus;
  created_at: Date;
  email: string;
  first_name: string;
  last_name: string;
}

export interface IncomingCandidateInvite extends OutgoingCandidateInvite {
  initiator: {
    company_name: string;
    company_information: string;
    company_type: string;
  };
}

export interface OutgoingCompanyInvite {
  id: string;
  status: CompanyInviteStatus;
  created_at: Date;
  email: string;
  first_name: string;
  last_name: string;
  company_name: string;
  target: Organization;
  previously_represented: boolean;
  position_title: string;
  represented_date: string;
}


export interface IncomingCompanyInvite extends OutgoingCompanyInvite {
  candidate_profile: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    position_title?: string;
  };
}

export interface CreateCompanyInviteDto {
  email: string;
  first_name: string;
  last_name: string;
  company_name: string;
  target_type: TenantType;
  previously_represented: boolean;
  position_title?: string;
  represented_date?: string;
}

export interface CreateCandidateInviteDto {
  email: string;
  first_name: string;
  last_name: string;
}

export interface InviteCandidatesResponse {
  email: string;
  result: 'success' | 'error';
  error: string | null;
}

export interface InviteCandidatesDTO {
  invites: CreateCandidateInviteDto[];
}

export interface GetCandidateInvitesProps {
  page: number;
  per_page: number;
  search_query?: string;
}

export interface GetCompanyInvitesProps {
  page: number;
  per_page: number;
  search_query?: string;
}

export interface ActionCandidateInviteProps {
  inviteId: string;
  action: 'accept' | 'decline';
}
export interface ActionCompanyInviteProps {
  inviteId: string;
  action: 'accept' | 'decline';
}

export interface CancelInviteProps {
  inviteId: string;
}

export type UploadProfileDocumentData = {
  type: DocumentType;
  file_id: string;
  visibility_rule?: VisibilityRule;
  expiry_date?: string;
  name: string;
};

export type ICandidateProfileDocument = {
  id: string;
  document: DocumentFile;
  candidate_profile: CandidateProfile;
  visibility_rule?: VisibilityRule;
  expiry_date?: string;
};
